import { useQueryClient } from "@tanstack/react-query";
import { useOrganization } from "@clerk/clerk-react";
import { useCallback, useMemo } from "react";
import { Product } from "@/types/product";

// Type definitions for different cache data formats
type ProductArray = Product[];
type ProductSearchResult = {
  products: Product[];
  totalCount: number;
  hasMore: boolean;
  limit: number;
  offset: number;
};

// Type guards for robust type checking
const isProductArray = (data: unknown): data is ProductArray => {
  return (
    Array.isArray(data) &&
    data.every(
      (item) =>
        typeof item === "object" &&
        item !== null &&
        "uid" in item &&
        typeof item.uid === "string",
    )
  );
};

const isProductSearchResult = (data: unknown): data is ProductSearchResult => {
  return (
    typeof data === "object" &&
    data !== null &&
    "products" in data &&
    Array.isArray((data as any).products) &&
    "totalCount" in data &&
    typeof (data as any).totalCount === "number"
  );
};

// Helper function to extract products from any cached data format
const extractProductsFromCacheData = (data: unknown): Product[] | null => {
  if (isProductArray(data)) {
    return data;
  }

  if (isProductSearchResult(data)) {
    return data.products;
  }

  return null;
};

/**
 * Enhanced product caching using TanStack Query's built-in cache system.
 * Automatically handles user/org switching, memory management, and cache invalidation.
 */
export const useProductQueryCache = () => {
  const queryClient = useQueryClient();
  const { organization } = useOrganization();

  // Generate scoped query keys that automatically clear when user/org changes
  const createProductQueryKey = useCallback(
    (identifier: string, ...additionalKeys: any[]) => {
      return ["products", organization?.id, identifier, ...additionalKeys];
    },
    [organization?.id],
  );

  // Get products from cache
  const getCachedProducts = useCallback(
    (queryKey: string[]): Product[] | undefined => {
      return queryClient.getQueryData<Product[]>(queryKey);
    },
    [queryClient],
  );

  // Set products in cache
  const setCachedProducts = useCallback(
    (queryKey: string[], products: Product[]) => {
      queryClient.setQueryData<Product[]>(queryKey, products);
    },
    [queryClient],
  );

  // Add products to an existing cache entry
  const addToCache = useCallback(
    (baseQueryKey: string[], newProducts: Product[]) => {
      if (newProducts.length === 0) return;

      const existingProducts = getCachedProducts(baseQueryKey) || [];

      // Create a Map for efficient deduplication by uid
      const productMap = new Map<string, Product>();

      // Add existing products first
      existingProducts.forEach((product) => {
        productMap.set(product.uid, product);
      });

      // Add/overwrite with new products
      let addedCount = 0;
      newProducts.forEach((product) => {
        if (!productMap.has(product.uid)) {
          addedCount++;
        }
        productMap.set(product.uid, product);
      });

      if (addedCount > 0) {
        const mergedProducts = Array.from(productMap.values());
        setCachedProducts(baseQueryKey, mergedProducts);
      }
    },
    [getCachedProducts, setCachedProducts],
  );

  // Get a single product by ID from any cached product list
  const getProductFromCache = useCallback(
    (productId: string): Product | null => {
      // Check all product-related queries in cache
      const queryCache = queryClient.getQueryCache();
      const productQueries = queryCache
        .getAll()
        .filter(
          (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey[0] === "products" &&
            query.queryKey[1] === organization?.id,
        );

      for (const query of productQueries) {
        const data = query.state.data;
        const products = extractProductsFromCacheData(data);

        if (products) {
          const product = products.find((p) => p.uid === productId);
          if (product) {
            return product;
          }
        }
      }
      return null;
    },
    [queryClient, organization?.id],
  );

  // Get multiple products by IDs from cache, maintaining input order
  const getProductsFromCache = useCallback(
    (productIds: string[]): (Product | null)[] => {
      return productIds.map((id) => getProductFromCache(id));
    },
    [getProductFromCache],
  );

  // Invalidate all product caches (useful for refresh)
  const invalidateProductCaches = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["products", organization?.id],
      exact: false,
    });
  }, [queryClient, organization?.id]);

  // Get cache stats
  const getCacheStats = useCallback(() => {
    const queryCache = queryClient.getQueryCache();
    const productQueries = queryCache
      .getAll()
      .filter(
        (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === "products" &&
          query.queryKey[1] === organization?.id,
      );

    const totalProducts = productQueries.reduce((count, query) => {
      const products = extractProductsFromCacheData(query.state.data);
      return count + (products?.length || 0);
    }, 0);

    const uniqueProducts = new Set<string>();
    productQueries.forEach((query) => {
      const products = extractProductsFromCacheData(query.state.data);
      products?.forEach((product) => uniqueProducts.add(product.uid));
    });

    return {
      queryCount: productQueries.length,
      totalProducts,
      uniqueProducts: uniqueProducts.size,
      cacheKeys: productQueries.map((q) => q.queryKey),
    };
  }, [queryClient, organization?.id]);

  // Memoized query key generators for common use cases
  const queryKeys = useMemo(
    () => ({
      initial: () => createProductQueryKey("initial"),
      search: (
        query: string,
        limit: number,
        offset: number,
        inStockOnly?: boolean,
        skipCorrection?: boolean,
        llmRerank?: boolean,
        rerankVariant?: string,
        engine?: string,
      ) =>
        createProductQueryKey(
          "search",
          query,
          limit,
          offset,
          inStockOnly ?? false,
          skipCorrection ?? false,
          llmRerank ?? false,
          rerankVariant ?? "search",
          engine ?? "postgres",
        ),
      recent: () => createProductQueryKey("recent"),
      lookup: (ids: string[]) =>
        createProductQueryKey("lookup", [...ids].sort().join(",")),
    }),
    [createProductQueryKey],
  );

  return {
    // Query key generators
    queryKeys,
    createProductQueryKey,

    // Cache operations
    getCachedProducts,
    setCachedProducts,
    addToCache,
    getProductFromCache,
    getProductsFromCache,

    // Cache management
    invalidateProductCaches,
    getCacheStats,
  };
};
